import React, { FC, useContext } from 'react';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { DropTarget } from 'react-dnd';

import { BareContentTemplateLegacy, FeedEditorElementType } from '@go-shared/types/contentTemplate';

import './FeedEditorHotspot.scss';
import { CONTENT_DND_TYPE } from './DraggableContentItem';
import FeedEditorColumn from './FeedEditor/Layout/FeedEditorColumn';
import FeedEditorRow from './FeedEditor/Layout/FeedEditorRow';

import { createRow, hotspotTarget, collectTarget } from '@go-client/lib/feedEditor';
import { Event } from '@go-shared/types/event';
import { Language } from '@go-shared/types/info';
import { BubbleButton } from './BubbleUI';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';

export interface FeedEditorHotspotProps extends NewLayoutComponentActions {
  area: FeedArea;
  contentTemplate?: BareContentTemplateLegacy;
  connectDropTarget?: any;
  isOver?: boolean;
  highlightAreas: boolean;
  onDrop?: (contentTemplate: BareContentTemplateLegacy) => void;
  actionButton?: () => JSX.Element;
  isChild?: boolean;
  onContentChange: (area: FeedArea, contentTemplate: BareContentTemplateLegacy) => void;
  onElementChanged: (area: FeedArea) => void;
  events?: Event[];
  noChilds?: boolean;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  activeElem?: string;
  setActiveElement?: (id: string) => void;
  editorLocked: boolean;
  mcmKey: string;
}

export interface NewLayoutComponentActions {
  onRowCreated: (parentArea: FeedArea, idx: number, newArea: FeedArea) => void;
  onRowMove: (parentArea: FeedArea, idx: number, direction: 1 | -1) => void;
  onColumnCreated: (parentArea: FeedArea, idx: number, newArea: FeedArea) => void;
  onElementCreated: (area: FeedArea, elementType: FeedEditorElementType) => void;
  onAreaDeleted: (area: FeedArea) => void;
  onElementChanged: (area: FeedArea) => void;
}

/**
 * FIXME PLEASE
 * This is a very big chapuza, needs to be fixed
 * Limits the bottom box rendering based on the number of events rendered
 * in the rundown, if the number of events is higher than this parameter,
 *  PRD bottom box won't be rendered
 */
const EVENTS_TRESHOLD = 20;

const FeedEditorHotspot: FC<FeedEditorHotspotProps> = ({
  area,
  isOver,
  highlightAreas,
  contentTemplate,
  actionButton,
  onRowCreated,
  onColumnCreated,
  onRowMove: onRowMove,
  isChild,
  onContentChange,
  onElementCreated,
  onAreaDeleted,
  onElementChanged,
  noChilds,
  selectedLanguage,
  showMergedVars,
  activeElem,
  setActiveElement,
  editorLocked,
  mcmKey,
}) => {
  let style = Object.assign(
    {
      top: area.rectangle.y,
      left: area.rectangle.x,
      width: area.rectangle.width,
      height: noChilds ? 'auto' : area.rectangle.height,
    },
    area.style
  );

  let { dailyProgram } = useContext(FeedEditorCtx);
  if (dailyProgram === undefined) {
    return <div>Loading ...</div>;
  }

  const { events } = dailyProgram;

  return (
    <div
      className={`ContentBlock
${isOver ? 'bg-green' : ''} ${area.className} ${isChild ? '' : 'absolute'} ${
        selectedLanguage ? `lang-${selectedLanguage.key}` : ''
      }`}
      style={style}
    >
      {actionButton && actionButton()}
      {!noChilds &&
        area.children &&
        area.children.map((child, i) => {
          if (child.contentType === 'column') {
            return (
              <FeedEditorColumn
                key={child._id}
                parent={child}
                parentIdx={i}
                selectedLanguage={selectedLanguage}
                showMergedVars={showMergedVars}
                highlightAreas={highlightAreas}
                onElementCreated={onElementCreated}
                onRowCreated={onRowCreated}
                onColumnCreated={onColumnCreated}
                onRowMove={onRowMove}
                onElementChanged={onElementChanged}
                onDrop={() => {
                  /*
                  This is a NOOP right now, you are not able to place content into
                  rows, only columns, if you want to make a full width box, you should do this:

                  Create a Row => create a column => place content into the full width column
                  */
                  /* NOOP */
                }}
                onContentChange={onContentChange}
                onAreaDeleted={onAreaDeleted}
                editorLocked={editorLocked}
                dailyProgram={dailyProgram!}
                mcmKey={mcmKey}
              />
            );
          }
          if (child.contentType === 'row') {
            if (events && events.length >= EVENTS_TRESHOLD && child.name === 'PRD_rundown_bottom') {
              return <React.Fragment key={child._id}></React.Fragment>;
            }
            return (
              <FeedEditorRow
                key={child._id}
                onElementChanged={onElementChanged}
                onAreaDeleted={onAreaDeleted}
                parent={child}
                parentIdx={i}
                childCount={(area.children || []).length}
                onElementCreated={onElementCreated}
                onRowCreated={onRowCreated}
                onColumnCreated={onColumnCreated}
                onRowMove={onRowMove}
                onDrop={ct => onContentChange(child, ct)}
                onContentChange={onContentChange}
                selectedLanguage={selectedLanguage}
                showMergedVars={showMergedVars}
                highlightAreas={highlightAreas}
                area={area}
                activeElem={activeElem}
                setActiveElement={setActiveElement}
                editorLocked={editorLocked}
                dailyProgram={dailyProgram!}
                mcmKey={mcmKey}
              />
            );
          }
        })}
      {contentTemplate && (
        <div>
          <div dangerouslySetInnerHTML={{ __html: contentTemplate.content }} />
        </div>
      )}
      {!editorLocked &&
        !area.isLocked &&
        area.contentType !== 'venue-rundown' &&
        area.contentType !== 'venue-meal-periods' &&
        area.children?.length === 0 && (
          <div className="w-full flex justify-center mt-2 absolute opacity-0 button-plus">
            <BubbleButton
              variant="lime"
              onClick={() => onRowCreated(area, 0, createRow())}
              appearance="circled"
              className="z-10 p-0-i text-xl"
              icon="plus"
              style={{ width: 30, height: 30 }}
            />
          </div>
        )}
    </div>
  );
};

export default DropTarget<FeedEditorHotspotProps>(
  CONTENT_DND_TYPE,
  hotspotTarget,
  collectTarget
)(FeedEditorHotspot);
